import {
  SOCIAL_TOAST_KEY,
  error_reason_no_role,
  error_reason_ip_blocked,
  error_reason_invalid_role,
  success_reason_social_login,
  error_reason_account_blocked,
  error_reason_account_inactive,
  error_reason_social_auth_failed,
  error_reason_social_email_not_found,
} from "../../utils/GlobalConstants";
import { routes } from "../../routes";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { syncUserData } from "../../services/userSync.service";
import { toggleOnboard } from "../../redux/slices/onBoardingSlice";
import { userSocialToken } from "../../api/modules/b2droppie.class";
import idleSessionService from "../../services/idleSession.service";
import { getCookie, setCookie } from "../../services/cookies.service";
import { clearAuth, updateUserData } from "../../redux/slices/authSlice";

const useUserSession = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ success: "", error: "" });
  const [userSocialTokenData, setUserSocialTokenData] = useState(false);

  const user = useSelector((state) => state?.auth?.user);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const reFetchKey = useSelector((state) => state.theme.reFetchKey);
  const verification = useSelector((state) => state.verification);

  const searchParams = new URLSearchParams(location.search);

  const socialCode = searchParams.get("code");
  const cameFromProtected = !!location.state?.from;

  const fetchUserData = async () => {
    if (!isLoggedIn) return;

    setLoading(true);

    try {
      const user = await syncUserData();

      if (user) {
        dispatch(updateUserData(user));
      }

      const hasRedirected = sessionStorage.getItem("freeBoxesRedirected");

      if (
        !hasRedirected &&
        user?.free_boxes &&
        user?.free_boxes_remaining > 0 &&
        location.pathname !== `${routes.ACCOUNT}/${routes.ACCOUNT_FREEBOXES}`
      ) {
        sessionStorage.setItem("freeBoxesRedirected", "true");

        navigate(`${routes.ACCOUNT}/${routes.ACCOUNT_FREEBOXES}`, {
          replace: true,
        });
      }

      if (!user) {
        dispatch(clearAuth());
        return;
      }

      const expirationTime = new Date().getTime() + 24 * 60 * 60 * 1000;

      setCookie("user", JSON.stringify(user), expirationTime);
    } catch (err) {
      console.error("Failed to fetch user data:", err);

      dispatch(clearAuth());
    } finally {
      setLoading(false);
    }
  };

  const updateUserSocialToken = async (token) => {
    setLoading(true);

    try {
      const data = await userSocialToken({
        code: token,
      });

      if (data) {
        const user = await syncUserData();

        if (user) {
          dispatch(updateUserData(user));
        }

        setUserSocialTokenData((prev) => !prev);
      }
    } catch (err) {
      console.error("Failed social login:", err);
    } finally {
      setLoading(false);
    }
  };

  // =========================================
  // Fetch User
  // =========================================

  useEffect(() => {
    if (reFetchKey === "mine" || location.pathname) {
      fetchUserData();
    }
  }, [location.pathname, reFetchKey, userSocialTokenData]);

  // =========================================
  // Social/Auth Toasts
  // =========================================

  useEffect(() => {
    const path = location.pathname;
    const token = searchParams.get("code");
    const reason = searchParams.get("reason");

    const stored = JSON.parse(localStorage.getItem(SOCIAL_TOAST_KEY));

    const now = Date.now();
    const EXPIRY = 10000;

    let uniqueKey = null;
    let toastData = null;

    const reasonMap = {
      auth_failed: {
        error: error_reason_social_auth_failed,
        login: true,
      },

      email_not_found: {
        error: error_reason_social_email_not_found,
        login: true,
      },

      social_success: {
        success: success_reason_social_login,
      },

      ip_blocked: {
        error: error_reason_ip_blocked,
      },

      account_inactive: {
        error: error_reason_account_inactive,
      },

      account_blocked: {
        error: error_reason_account_blocked,
      },

      no_role: {
        error: error_reason_no_role,
      },

      invalid_role: {
        error: error_reason_invalid_role,
      },
    };

    if (reason && reasonMap[reason]) {
      uniqueKey = `toast_${reason}`;

      toastData = {
        success: reasonMap[reason].success || "",
        error: reasonMap[reason].error || "",
      };

      if (reasonMap[reason].login) {
        dispatch(toggleOnboard({ login: true }));
      }
    }

    if (path === "/social/callback" && token) {
      uniqueKey = `social_callback_${token}`;

      toastData = {
        success: success_reason_social_login,
        error: "",
      };

      updateUserSocialToken(token);
    }

    if (path === "/user-verification/callback" && token) {
      updateUserSocialToken(token);
    }

    if (path === "/user-verification/expired") {
      uniqueKey = "verification_expired";

      toastData = {
        success: "",
        error: "Link has been expired",
      };
    }

    if (path === "/user-verification/error" && reason && reasonMap[reason]) {
      uniqueKey = `verification_error_${reason}`;

      toastData = {
        success: "",
        error: reasonMap[reason].error || "An unexpected error occurred.",
      };
    }

    if (
      !uniqueKey ||
      (stored?.key === uniqueKey && now - stored.timestamp < EXPIRY)
    ) {
      return;
    }

    setToast(toastData);

    localStorage.setItem(
      SOCIAL_TOAST_KEY,
      JSON.stringify({
        key: uniqueKey,
        timestamp: now,
      }),
    );
  }, [location.pathname, location.search]);

  // =========================================
  // Verification Toast
  // =========================================

  useEffect(() => {
    if (
      !verification ||
      !verification.status ||
      location.pathname === `${routes.ACCOUNT}/${routes.ACCOUNT_DOCUMENTS}`
    ) {
      return;
    }

    const status = verification.status?.toLowerCase()?.trim();

    const backendMessage = verification?.message;

    let successMsg = "";
    let errorMsg = "";

    switch (status) {
      case "accepted":
        successMsg = backendMessage || "Your documents have been verified.";
        break;

      case "rejected":
        errorMsg =
          backendMessage ||
          "Your document was rejected. Please re-upload a valid document.";
        break;

      case "further_verification_requested":
        errorMsg = backendMessage || "Further verification is required.";
        break;

      default:
        errorMsg = backendMessage || "An unexpected error occurred.";
        break;
    }

    setToast({
      success: successMsg,
      error: errorMsg,
    });
  }, [verification]);

  // =========================================
  // Idle Tracking
  // =========================================

  useEffect(() => {
    if (isLoggedIn) {
      idleSessionService.startIdleTracking();
    } else {
      idleSessionService.stopIdleTracking();
    }

    return () => {
      idleSessionService.stopIdleTracking();
    };
  }, [isLoggedIn]);

  // =========================================
  // Protected Route Login Modal
  // =========================================

  useEffect(() => {
    if (cameFromProtected && !isLoggedIn) {
      dispatch(toggleOnboard({ login: true }));
    }
  }, [cameFromProtected, isLoggedIn]);

  return {
    user,
    loading,
    setLoading,
    toast,
    isLoggedIn,
    socialCode,
  };
};

export default useUserSession;
